import { DOCUMENT } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { enableProdMode, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";
import { RouteReuseStrategy } from "@angular/router";
import { AppRoutingModule } from "@app/app-routing.module";
import { AppComponent } from "@app/app.component";
import { VersionService } from "@app/core/services/version.service";
import { Constants } from "@core/constants";
import { CoreModule } from "@core/core.module";
import { GlobalErrorHandler } from "@core/error/global-error-handler";
import { AuthInterceptor } from "@core/services/auth-interceptor.service";
import { AuthService } from "@core/services/auth.service";
import { BrowserDetectionService } from "@core/services/browser-detection.service";
import { ConfigParamsAndScreenTextsService } from "@core/services/config-params-and-screen-texts.service";
import { WINDOW_PROVIDERS } from "@core/services/window.service";
import { CustomTranslateHttpLoader } from "@core/util/custom-translate-http-loader";
import { environment } from "@environment/environment";
import { IonicRouteStrategy, provideIonicAngular } from "@ionic/angular/standalone";
import { provideTranslateService, TranslateLoader, TranslateService } from "@ngx-translate/core";
import { LanguageService } from "@shared/services/language.service";
import { NgxPermissionsModule } from "ngx-permissions";
import { EMPTY, map, Observable, switchMap } from "rxjs";

// No module-global version needed anymore; VersionService is read at request-time by the
// translate loader. Keeping a module-global value introduced a race condition where
// early translation requests could observe "unknown" before `VersionService` was populated.

/**
 * Initialize the application with required services
 */
function initializeApp(
  authService: AuthService,
  configParamsAndScreenTextsService: ConfigParamsAndScreenTextsService,
  translateService: TranslateService,
  languageService: LanguageService,
  globalErrorHandler: GlobalErrorHandler,
  versionService: VersionService,
): () => Observable<void> {
  // Use Angular's DOCUMENT injection to get window (recommended approach)
  const document = inject(DOCUMENT);
  const window = document.defaultView ?? globalThis;

  if (window.location.href.includes("/#/")) {
    window.location.replace(window.location.href.replace("/#/", "/"));
  }
  if (window.location.href.includes("status_meldingen")) {
    return () => {
      globalErrorHandler.handleRejectedDatabaseLogin();
      return EMPTY;
    };
  } else {
    return () => {
      const lang = languageService.determineLanguage();
      return versionService.setOsirisVersion().pipe(
        switchMap(() => translateService.use(lang)),
        switchMap(() => authService.authorize()),
        switchMap(() => configParamsAndScreenTextsService.loadConfigParamsAndScreenTexts()),
        map(() => void 0),
      );
    };
  }
}

const translateHttpLoaderFactory = (httpClient: HttpClient, versionService: VersionService): TranslateLoader => {
  return new CustomTranslateHttpLoader(
    httpClient,
    "./assets/translations/",
    ".json",
    environment.production,
    versionService,
  );
};

/**
 * Check browser compatibility and show warning if needed
 */
function checkBrowserCompatibility(): void {
  const browserDetection = new BrowserDetectionService();
  const browserInfo = browserDetection.detectBrowser();
  const hasEssentialFeatures = browserDetection.checkEssentialFeatures();

  // Show warning overlay if browser is not supported or lacks essential features
  if (!browserInfo.isSupported || !hasEssentialFeatures) {
    // Show warning after a short delay to ensure the app has started
    setTimeout(() => {
      browserDetection.showBrowserWarningOverlay(browserInfo);
    }, 1000);
  }
}

/**
 * Start the Angular application
 */
function startApplication(): void {
  if (environment.production) {
    enableProdMode();
  }

  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(BrowserModule, NgxPermissionsModule.forRoot(), AppRoutingModule, CoreModule),
      provideAppInitializer(() => {
        const initializerFn = initializeApp(
          inject(AuthService),
          inject(ConfigParamsAndScreenTextsService),
          inject(TranslateService),
          inject(LanguageService),
          inject(GlobalErrorHandler),
          inject(VersionService),
        );
        return initializerFn();
      }),
      {
        provide: RouteReuseStrategy,
        useClass: IonicRouteStrategy,
      },
      {
        provide: ErrorHandler,
        useClass: GlobalErrorHandler,
      },
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      WINDOW_PROVIDERS,
      provideHttpClient(withInterceptorsFromDi()),
      provideIonicAngular({}),
      provideTranslateService({
        fallbackLang: Constants.LANGUAGE_NL,
        loader: {
          provide: TranslateLoader,
          useFactory: translateHttpLoaderFactory,
          deps: [HttpClient, VersionService],
        },
      }),
    ],
  }).catch((err) => console.log(err));
}

startApplication();
checkBrowserCompatibility();
